<script setup lang="ts">
import type { CountriesList } from '@/types/UserData';
import IconWrapper from '@/components/base/IconWrapper.vue';

type Props = {
  label?: string;
  id?: string;
  required?: boolean;
  selectedCountry?: string;
  initialDdi?: string;
  initialPhone?: string;
};

const {
  label,
  id = 'phone',
  required = false,
  selectedCountry,
  initialDdi,
  initialPhone,
} = defineProps<Props>();

const { userPhone, userPhoneDdi } = useUserData();
const { allCountriesList } = storeToRefs(useCountriesStore());
const { visitorOrUserCountry } = useParameters();

const computedCountry = computed<string>(() => {
  return selectedCountry || visitorOrUserCountry.value;
});

const countriesSortDdi = computed(() => {
  if (!allCountriesList?.value) {
    return;
  }

  return allCountriesList.value.sort((a: any, b: any) => a.ddi - b.ddi);
});

const formFieldPhone = ref<string>(initialPhone || userPhone.value);

const selectedDdi = ref<CountriesList>({
  id: 0,
  code: '',
  ddi: '',
});

const maskByCountryPhone = computed<string[] | string>(() => {
  if (!selectedDdi.value.code) return '';

  // @ts-ignore
  const mask = phoneMaksInAlpha3[selectedDdi.value.code] ?? '';

  return mask || '';
});

const ddiError = ref<string>('');

function defaultSelectedDdi() {
  const userOpts = computed(() => {
    if (!allCountriesList?.value) {
      return;
    }

    if (initialDdi) {
      return allCountriesList?.value.find(
        (country: any) => country.ddi === initialDdi,
      );
    }

    if (userPhoneDdi.value) {
      return allCountriesList?.value.find(
        (country: any) => country.ddi === userPhoneDdi.value,
      );
    }

    return allCountriesList?.value.find(
      (country: any) => country.code === computedCountry.value,
    );
  });

  if (userOpts.value) {
    selectedDdi.value = {
      id: userOpts.value.id,
      code: userOpts.value.code,
      ddi: userOpts.value.ddi,
    };
  }

  const unmaskedUserPhone = userPhone.value.replace(/\D/g, '');

  if (
    selectedDdi.value.code === 'BRA' &&
    unmaskedUserPhone.startsWith('55') &&
    unmaskedUserPhone.length > 11
  ) {
    formFieldPhone.value = unmaskedUserPhone.substring(2);
  }
}

function removeMask(str: string, number?: boolean) {
  if (number) {
    return str.replace(/[^\d]/gi, '');
  }

  return str.replace(/[^\w]/gi, '');
}

onMounted(() => {
  defaultSelectedDdi();
});

onActivated(() => {
  defaultSelectedDdi();
});

watch(
  () => [allCountriesList.value, userPhoneDdi.value, computedCountry.value],
  () => {
    defaultSelectedDdi();
  },
);

const selectedPhoneNumber = computed(() => {
  return removeMask(formFieldPhone.value ?? '');
});

const selectedPhoneDdi = computed(() => {
  return selectedDdi.value.ddi ?? '';
});

defineExpose({
  selectedPhoneNumber,
  selectedPhoneDdi,
});
</script>

<template>
  <div :class="$style.inputFullPhone">
    <div class="form-group">
      <label
        v-if="label"
        for="phone"
      >
        {{ label }} {{ initialDdi }} {{ initialPhone }}
      </label>
      <BaseFormInput
        :id="id"
        v-model="formFieldPhone"
        autocomplete="off"
        :class="{ error: ddiError }"
        :data-maska="maskByCountryPhone"
        :ddi="!!allCountriesList"
        :initial-value="formFieldPhone"
        :label="$t('auth.register.phone')"
        :name="id"
        :required="required"
        type="text"
      >
        <template
          v-if="allCountriesList"
          #ddi
        >
          <div class="absolute">
            <HeadlessListbox
              v-model="selectedDdi"
              as="div"
              class="relative inline-block text-left"
            >
              <HeadlessListboxButton
                class="inline-flex w-full justify-center items-center rounded-none px-4 py-2 text-sm font-medium"
                @click="ddiError = ''"
              >
                <IconWrapper
                  v-if="selectedDdi.code"
                  class="text-xl mr-2"
                  :name="`flags/${selectedDdi.code}`"
                />
                {{ selectedDdi.ddi || 'DDI' }}
                <NuxtIcon
                  v-if="!selectedDdi.code"
                  class="ml-3 opacity-30"
                  name="arrows/angle_down"
                />
              </HeadlessListboxButton>
              <HeadlessListboxOptions
                class="absolute right-50 mt-2 w-24 origin-top-right divide-y divide-gray-100 rounded-md bg-auth-bg-inputs shadow-lg ring-1 ring-black/5 focus:outline-none z-[999] overflow-hidden h-[200px]"
              >
                <div class="px-1 py-1 relative overflow-auto h-full">
                  <HeadlessListboxOption
                    v-for="option in countriesSortDdi"
                    :key="option.id"
                    :value="option"
                  >
                    <span
                      class="group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer"
                    >
                      <IconWrapper
                        class="text-xl mr-2"
                        :name="`flags/${option.code}`"
                      />
                      {{ option.ddi }}
                    </span>
                  </HeadlessListboxOption>
                </div>
              </HeadlessListboxOptions>
            </HeadlessListbox>
          </div>
        </template>
      </BaseFormInput>
      <div
        v-if="ddiError"
        class="error-message"
      >
        {{ ddiError }}
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.inputFullPhone {
  @apply relative;
  .myPhone {
    @apply text-auth-texts flex items-center gap-x-2 text-base flex-wrap gap-y-0 leading-6;
    .changeButton {
      @apply text-xs ml-1 inline-flex text-auth-links;
    }
  }
}
</style>
